import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box, Typography, Card, CardContent, CardActionArea, Stack, Skeleton,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Chip, Badge,
  Menu, MenuItem, Divider, Tabs, Tab
} from '@mui/material'
import { MoreVert, Share, Edit, ExitToApp, ContentCopy, Forum, MarkEmailRead, Email, PersonAdd } from '@mui/icons-material'
import QuickActions from '@/components/QuickActions'
import FamilyColorBadge from '@/components/FamilyColorBadge'
import UserNameChip from '@/components/UserNameChip'
import { useSnackbar } from 'notistack'
import { useAuth } from '@/contexts/AuthContext'
import { apiClient } from '@/services/apiClient'
import { Group, PrayerRequest, Invite, Discussion, SendInAppInviteResult } from '@/types'
import { PrayerIcon } from '@/components/PrayerIcon'
import { useGroupHub } from '@/hooks/useGroupHub'
import { useGroupNavigation } from '@/contexts/GroupNavigationContext'
import { buildFamilyColorMap, getFamilyColor } from '@/utils/familyColors'

export default function GroupDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const [group, setGroup] = useState<Group | null>(null)
  const [prayerRequests, setPrayerRequests] = useState<PrayerRequest[]>([])
  const [discussions, setDiscussions] = useState<Discussion[]>([])
  const [loading, setLoading] = useState(true)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [editOpen, setEditOpen] = useState(false)
  const [editName, setEditName] = useState('')
  const [inviteOpen, setInviteOpen] = useState(false)
  const [invite, setInvite] = useState<Invite | null>(null)
  const [generatingInvite, setGeneratingInvite] = useState(false)
  const [inviteTab, setInviteTab] = useState(0)
  const [emailInvite, setEmailInvite] = useState('')
  const [sendingEmailInvite, setSendingEmailInvite] = useState(false)
  const { activeTab, enterGroup, setPrayerBadgeCount, setDiscussionBadgeCount } = useGroupNavigation()

  const loadData = useCallback(async () => {
    if (!id) return
    try {
      const [g, pr, disc] = await Promise.all([
        apiClient.get<Group>(`/api/groups/${id}`),
        apiClient.get<PrayerRequest[]>(`/api/prayerrequests/group/${id}`),
        apiClient.get<Discussion[]>(`/api/discussions/group/${id}`)
      ])
      setGroup(g)
      setPrayerRequests(pr)
      setDiscussions(disc)
    } catch {
      enqueueSnackbar('Failed to load group', { variant: 'error' })
      navigate('/groups')
    } finally {
      setLoading(false)
    }
  }, [id, enqueueSnackbar, navigate])

  const { groupedPrayerRequests, answeredPrayerRequests } = useMemo(() => {
    const unanswered = prayerRequests.filter(pr => !pr.answer)
    const answered = prayerRequests.filter(pr => !!pr.answer)

    // Group unanswered by family
    const familyMap = new Map<string, { familyId: string | null; familyName: string | null; requests: PrayerRequest[] }>()
    for (const pr of unanswered) {
      const key = pr.familyId ?? ''
      if (!familyMap.has(key)) {
        familyMap.set(key, { familyId: pr.familyId ?? null, familyName: pr.familyName ?? null, requests: [] })
      }
      familyMap.get(key)!.requests.push(pr)
    }
    // Sort family groups by most recent prayer request (already sorted desc from API)
    const groupedPrayerRequests = Array.from(familyMap.values()).sort((a, b) => {
      const aDate = new Date(a.requests[0]?.createdDate ?? 0).getTime()
      const bDate = new Date(b.requests[0]?.createdDate ?? 0).getTime()
      return bDate - aDate
    })

    // Sort answered prayers by most recently updated
    const answeredPrayerRequests = [...answered].sort((a, b) => {
      const aDate = new Date(a.lastModifiedDate ?? a.createdDate).getTime()
      const bDate = new Date(b.lastModifiedDate ?? b.createdDate).getTime()
      return bDate - aDate
    })

    return { groupedPrayerRequests, answeredPrayerRequests }
  }, [prayerRequests])

  // Build a stable color map from all families/users known in this group (members + PR authors + discussion authors)
  const familyColorMap = useMemo(() => {
    const ids: (string | undefined)[] = []
    group?.members.forEach(m => ids.push(m.familyId ?? m.userId))
    prayerRequests.forEach(pr => ids.push(pr.familyId ?? pr.userId))
    discussions.forEach(d => ids.push(d.familyId ?? d.userId))
    return buildFamilyColorMap(ids)
  }, [group, prayerRequests, discussions])

  useEffect(() => { loadData() }, [loadData])

  // Register group in navigation context
  useEffect(() => {
    if (id) enterGroup(id)
  }, [id, enterGroup])

  // Update badge counts
  useEffect(() => {
    setPrayerBadgeCount(prayerRequests.filter(p => p.isNew).length)
  }, [prayerRequests, setPrayerBadgeCount])

  useEffect(() => {
    setDiscussionBadgeCount(discussions.filter(d => d.isNew).length)
  }, [discussions, setDiscussionBadgeCount])

  // Real-time updates via SignalR
  const groupIdsForHub = useMemo(() => id ? [id] : [], [id])
  useGroupHub(groupIdsForHub, {
    onPrayerRequestCreated: (pr) => {
      setPrayerRequests(prev => {
        if (prev.some(p => p.id === pr.id)) return prev
        const incoming = pr.userId !== user?.userId ? { ...pr, isNew: true } : pr
        return [incoming, ...prev]
      })
    },
    onPrayerRequestUpdated: (pr) => {
      setPrayerRequests(prev => prev.map(p => {
        if (p.id !== pr.id) return p
        return pr.userId !== user?.userId ? { ...pr, isNew: true } : pr
      }))
    },
    onPrayerRequestDeleted: (prId) => {
      setPrayerRequests(prev => prev.filter(p => p.id !== prId))
    },
    onCommentAdded: ({ prayerRequestId, comment }) => {
      setPrayerRequests(prev => prev.map(p =>
        p.id === prayerRequestId
          ? { ...p, commentCount: p.commentCount + 1, isNew: comment.userId !== user?.userId ? true : p.isNew }
          : p
      ))
    },
    onDiscussionCreated: (disc) => {
      setDiscussions(prev => {
        if (prev.some(d => d.id === disc.id)) return prev
        const incoming = disc.userId !== user?.userId ? { ...disc, isNew: true } : disc
        return [incoming, ...prev]
      })
    },
    onDiscussionUpdated: (disc) => {
      setDiscussions(prev => prev.map(d => {
        if (d.id !== disc.id) return d
        return disc.userId !== user?.userId ? { ...disc, isNew: true } : disc
      }))
    },
    onDiscussionDeleted: (discId) => {
      setDiscussions(prev => prev.filter(d => d.id !== discId))
    },
    onDiscussionCommentAdded: ({ discussionId, comment }) => {
      setDiscussions(prev => prev.map(d =>
        d.id === discussionId
          ? { ...d, commentCount: d.commentCount + 1, isNew: comment.userId !== user?.userId ? true : d.isNew }
          : d
      ))
    },
  })

  const handleEdit = async () => {
    if (!editName.trim() || !id) return
    try {
      await apiClient.put(`/api/groups/${id}`, { name: editName.trim() })
      setGroup(prev => prev ? { ...prev, name: editName.trim() } : null)
      setEditOpen(false)
      enqueueSnackbar('Group updated', { variant: 'success' })
    } catch {
      enqueueSnackbar('Failed to update group', { variant: 'error' })
    }
  }

  const handleLeave = async () => {
    if (!id) return
    if (!confirm('Are you sure you want to leave this group?')) return
    try {
      await apiClient.post(`/api/groups/${id}/leave`)
      enqueueSnackbar('Left group', { variant: 'success' })
      navigate('/groups')
    } catch {
      enqueueSnackbar('Failed to leave group', { variant: 'error' })
    }
  }

  const handleGenerateInvite = async () => {
    if (!id) return
    setGeneratingInvite(true)
    try {
      const inv = await apiClient.post<Invite>(`/api/invites/group/${id}`, { expirationHours: 72 })
      setInvite(inv)
    } catch {
      enqueueSnackbar('Failed to generate invite', { variant: 'error' })
    } finally {
      setGeneratingInvite(false)
    }
  }

  const copyInviteLink = () => {
    if (!invite) return
    const link = `${window.location.origin}/invite/${invite.inviteCode}`
    navigator.clipboard.writeText(link)
    enqueueSnackbar('Invite link copied!', { variant: 'success' })
  }

  const handleSendEmailInvite = async () => {
    if (!emailInvite.trim() || !id) return
    setSendingEmailInvite(true)
    try {
      const result = await apiClient.post<SendInAppInviteResult>(`/api/invites/group/${id}/email`, { email: emailInvite.trim() })
      if (result.success) {
        enqueueSnackbar('Invite sent!', { variant: 'success' })
        setEmailInvite('')
      } else {
        enqueueSnackbar(result.error || 'Failed to send invite', { variant: 'error' })
      }
    } catch {
      enqueueSnackbar('Failed to send invite', { variant: 'error' })
    } finally {
      setSendingEmailInvite(false)
    }
  }

  const handleTogglePrayerUnread = async (_e: React.MouseEvent, pr: PrayerRequest) => {
    try {
      if (pr.isNew) {
        // Mark as viewed (explicit action)
        await apiClient.post(`/api/prayerrequests/${pr.id}/viewed`)
        setPrayerRequests(prev => prev.map(p => p.id === pr.id ? { ...p, isNew: false, isMarkedUnread: false } : p))
      } else {
        // Mark as unread
        await apiClient.post(`/api/prayerrequests/${pr.id}/unread`)
        setPrayerRequests(prev => prev.map(p => p.id === pr.id ? { ...p, isNew: true, isMarkedUnread: true } : p))
      }
    } catch {
      enqueueSnackbar('Failed to update read status', { variant: 'error' })
    }
  }

  const handleToggleDiscussionUnread = async (_e: React.MouseEvent, disc: Discussion) => {
    try {
      if (disc.isNew) {
        // Mark as viewed (explicit action)
        await apiClient.post(`/api/discussions/${disc.id}/viewed`)
        setDiscussions(prev => prev.map(d => d.id === disc.id ? { ...d, isNew: false, isMarkedUnread: false } : d))
      } else {
        // Mark as unread
        await apiClient.post(`/api/discussions/${disc.id}/unread`)
        setDiscussions(prev => prev.map(d => d.id === disc.id ? { ...d, isNew: true, isMarkedUnread: true } : d))
      }
    } catch {
      enqueueSnackbar('Failed to update read status', { variant: 'error' })
    }
  }

  if (loading) {
    return (
      <Box sx={{ pb: 10 }}>
        <Skeleton variant="text" width="60%" height={40} />
        <Skeleton variant="rounded" height={100} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={80} />
        <Skeleton variant="rounded" height={80} sx={{ mt: 1 }} />
      </Box>
    )
  }

  if (!group) return null

  return (
    <Box sx={{ pb: 10 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{group.name}</Typography>
        <IconButton onClick={e => setMenuAnchor(e.currentTarget)}>
          <MoreVert />
        </IconButton>
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
          <MenuItem onClick={() => { setMenuAnchor(null); setEditName(group.name); setEditOpen(true) }}>
            <Edit sx={{ mr: 1 }} fontSize="small" /> Edit Group
          </MenuItem>
          <MenuItem onClick={() => { setMenuAnchor(null); setInviteOpen(true); handleGenerateInvite() }}>
            <Share sx={{ mr: 1 }} fontSize="small" /> Invite Members
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => { setMenuAnchor(null); handleLeave() }} sx={{ color: 'error.main' }}>
            <ExitToApp sx={{ mr: 1 }} fontSize="small" /> Leave Group
          </MenuItem>
        </Menu>
      </Box>

      {/* Members */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Members ({group.members.length})
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {group.members.map(m => {
            const fc = getFamilyColor(familyColorMap, m.familyId ?? m.userId)
            const handleClick = m.userId !== user?.userId
              ? () => navigate(`/users/${m.userId}`)
              : () => navigate('/profile')
            return fc ? (
              <FamilyColorBadge
                key={m.userId}
                label={m.displayName}
                bgColor={fc.bg}
                textColor={fc.text}
                onClick={handleClick}
              />
            ) : (
              <Chip
                key={m.userId}
                label={m.displayName}
                size="small"
                color={m.userId === user?.userId ? 'primary' : 'default'}
                variant={m.userId === user?.userId ? 'filled' : 'outlined'}
                onClick={handleClick}
              />
            )
          })}
        </Box>
      </Box>

      {activeTab === 'prayers' && (<>
      {/* Prayer Requests */}
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
        Prayer Requests
      </Typography>
      {prayerRequests.length === 0 ? (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary" gutterBottom>
              No prayer requests yet. Be the first to share one!
            </Typography>
            <Button variant="contained" startIcon={<PrayerIcon />} onClick={() => navigate(`/groups/${id}/new-prayer`)}>
              New Prayer Request
            </Button>
          </CardContent>
        </Card>
      ) : (<>
        <Stack spacing={2}>
          {groupedPrayerRequests.map(familyGroup => (
            <Box key={familyGroup.familyId ?? 'no-family'}>
              {familyGroup.familyName && (() => {
                const fc = getFamilyColor(familyColorMap, familyGroup.familyId)
                return (
                  <Box sx={{ display: 'inline-flex', mb: 0.5, px: 0.5 }}>
                    <FamilyColorBadge
                      label={familyGroup.familyName}
                      bgColor={fc?.bg}
                      textColor={fc?.text}
                    />
                  </Box>
                )
              })()}
              <Stack spacing={1}>
                {familyGroup.requests.map(pr => (
                  <Card key={pr.id} variant="outlined">
                    <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                      <CardActionArea onClick={() => navigate(`/prayer-requests/${pr.id}`)} sx={{ flex: 1, minWidth: 0 }}>
                        <CardContent sx={{ py: 1.5 }}>
                          <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>{pr.title}</Typography>
                          <Typography variant="body2" color="text.secondary" noWrap>{pr.description}</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                              <UserNameChip
                                userId={pr.userId}
                                displayName={pr.userDisplayName}
                                bgColor={getFamilyColor(familyColorMap, pr.familyId ?? pr.userId)?.bg}
                                textColor={getFamilyColor(familyColorMap, pr.familyId ?? pr.userId)?.text}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {new Date(pr.createdDate).toLocaleDateString()}
                              </Typography>
                            </Box>
                        </CardContent>
                      </CardActionArea>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', pr: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleTogglePrayerUnread({} as React.MouseEvent, pr)}
                          title={pr.isNew ? 'Mark as viewed' : 'Mark as unread'}
                          sx={{ color: pr.isNew ? 'success.main' : 'action.disabled' }}
                        >
                          {pr.isNew ? (
                            <Badge
                              overlap="circular"
                              variant="dot"
                              color="error"
                              sx={{ '& .MuiBadge-badge': { minWidth: 10, height: 10, borderRadius: '50%', top: 2, right: 2 } }}
                            >
                              <MarkEmailRead fontSize="small" />
                            </Badge>
                          ) : (
                            <MarkEmailRead fontSize="small" />
                          )}
                        </IconButton>
                        {pr.commentCount > 0 && (
                          <Chip label={`${pr.commentCount}`} size="small" variant="outlined" />
                        )}
                      </Box>
                    </Box>
                  </Card>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>

        {answeredPrayerRequests.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
              Answered Prayers
            </Typography>
            <Stack spacing={1}>
              {answeredPrayerRequests.map(pr => (
                <Card key={pr.id} variant="outlined" sx={{ opacity: 0.85 }}>
                  <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                    <CardActionArea onClick={() => navigate(`/prayer-requests/${pr.id}`)} sx={{ flex: 1, minWidth: 0 }}>
                      <CardContent sx={{ py: 1.5 }}>
                        <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>{pr.title}</Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>{pr.description}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                            <UserNameChip
                              userId={pr.userId}
                              displayName={pr.userDisplayName}
                              bgColor={getFamilyColor(familyColorMap, pr.familyId ?? pr.userId)?.bg}
                              textColor={getFamilyColor(familyColorMap, pr.familyId ?? pr.userId)?.text}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {new Date(pr.lastModifiedDate ?? pr.createdDate).toLocaleDateString()}
                            </Typography>
                          </Box>
                      </CardContent>
                    </CardActionArea>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', pr: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleTogglePrayerUnread({} as React.MouseEvent, pr)}
                        title={pr.isNew ? 'Mark as viewed' : 'Mark as unread'}
                        sx={{ color: pr.isNew ? 'success.main' : 'action.disabled' }}
                      >
                        {pr.isNew ? (
                          <Badge
                            overlap="circular"
                            variant="dot"
                            color="error"
                            sx={{ '& .MuiBadge-badge': { minWidth: 10, height: 10, borderRadius: '50%', top: 2, right: 2 } }}
                          >
                            <MarkEmailRead fontSize="small" />
                          </Badge>
                        ) : (
                          <MarkEmailRead fontSize="small" />
                        )}
                      </IconButton>
                      <Chip label="Answered" size="small" color="success" />
                      {pr.commentCount > 0 && (
                        <Chip label={`${pr.commentCount}`} size="small" variant="outlined" />
                      )}
                    </Box>
                  </Box>
                </Card>
              ))}
            </Stack>
          </Box>
        )}
      </>)}

      </>)}

      {activeTab === 'discussions' && (<>
      {/* Discussions */}
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
        Discussions
      </Typography>
      {discussions.length === 0 ? (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary" gutterBottom>
              No discussions yet. Start a conversation!
            </Typography>
            <Button variant="contained" startIcon={<Forum />} onClick={() => navigate(`/groups/${id}/new-discussion`)}>
              New Discussion
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={1}>
          {discussions.map(disc => (
            <Card key={disc.id} variant="outlined">
              <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                <CardActionArea onClick={() => navigate(`/discussions/${disc.id}`)} sx={{ flex: 1, minWidth: 0 }}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>{disc.title}</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>{disc.description}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                        <UserNameChip
                          userId={disc.userId}
                          displayName={disc.userDisplayName}
                          bgColor={getFamilyColor(familyColorMap, disc.familyId ?? disc.userId)?.bg}
                          textColor={getFamilyColor(familyColorMap, disc.familyId ?? disc.userId)?.text}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {new Date(disc.createdDate).toLocaleDateString()}
                        </Typography>
                      </Box>
                  </CardContent>
                </CardActionArea>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', pr: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleToggleDiscussionUnread({} as React.MouseEvent, disc)}
                    title={disc.isNew ? 'Mark as viewed' : 'Mark as unread'}
                    sx={{ color: disc.isNew ? 'success.main' : 'action.disabled' }}
                  >
                    {disc.isNew ? (
                      <Badge
                        overlap="circular"
                        variant="dot"
                        color="error"
                        sx={{ '& .MuiBadge-badge': { minWidth: 10, height: 10, borderRadius: '50%', top: 2, right: 2 } }}
                      >
                        <MarkEmailRead fontSize="small" />
                      </Badge>
                    ) : (
                      <MarkEmailRead fontSize="small" />
                    )}
                  </IconButton>
                  {disc.commentCount > 0 && (
                    <Chip label={`${disc.commentCount}`} size="small" variant="outlined" />
                  )}
                </Box>
              </Box>
            </Card>
          ))}
        </Stack>
      )}
      </>)}

      <QuickActions
        onNewPrayer={() => navigate(`/groups/${id}/new-prayer`)}
        onNewDiscussion={() => navigate(`/groups/${id}/new-discussion`)}
        activeContext={activeTab}
      />

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Edit Group</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth label="Group Name" value={editName}
            onChange={e => setEditName(e.target.value)} margin="dense"
            onKeyDown={e => e.key === 'Enter' && handleEdit()}
          />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 0.5 }}>
            Members ({group.members.length})
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {group.members.map(m => {
              const fc = getFamilyColor(familyColorMap, m.familyId ?? m.userId)
              return fc ? (
                <FamilyColorBadge
                  key={m.userId}
                  label={m.displayName}
                  bgColor={fc.bg}
                  textColor={fc.text}
                />
              ) : (
                <Chip
                  key={m.userId}
                  label={m.displayName}
                  size="small"
                  color={m.userId === user?.userId ? 'primary' : 'default'}
                  variant={m.userId === user?.userId ? 'filled' : 'outlined'}
                />
              )
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={handleEdit} variant="contained" disabled={!editName.trim()}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Invite Dialog */}
      <Dialog open={inviteOpen} onClose={() => { setInviteOpen(false); setInvite(null); setInviteTab(0); setEmailInvite('') }} fullWidth maxWidth="xs">
        <DialogTitle>Invite to {group.name}</DialogTitle>
        <DialogContent>
          <Tabs value={inviteTab} onChange={(_, v) => setInviteTab(v)} sx={{ mb: 2 }}>
            <Tab icon={<Share fontSize="small" />} iconPosition="start" label="Link" />
            <Tab icon={<Email fontSize="small" />} iconPosition="start" label="Email" />
          </Tabs>
          {inviteTab === 0 && (
            <>
              {generatingInvite ? (
                <Skeleton variant="rounded" height={48} />
              ) : invite ? (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Share this link to invite people to your group. Expires in 72 hours.
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-all', fontFamily: 'monospace' }}>
                      {window.location.origin}/invite/{invite.inviteCode}
                    </Typography>
                    <IconButton size="small" onClick={copyInviteLink}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ) : null}
            </>
          )}
          {inviteTab === 1 && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Send an in-app invite to a user by their email address.
              </Typography>
              <TextField
                autoFocus fullWidth label="Email Address" type="email" value={emailInvite}
                onChange={e => setEmailInvite(e.target.value)} margin="dense"
                onKeyDown={e => e.key === 'Enter' && handleSendEmailInvite()}
              />
              <Button
                onClick={handleSendEmailInvite} variant="contained" fullWidth sx={{ mt: 1 }}
                disabled={sendingEmailInvite || !emailInvite.trim()}
                startIcon={<PersonAdd />}
              >
                {sendingEmailInvite ? 'Sending...' : 'Send Invite'}
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setInviteOpen(false); setInvite(null); setInviteTab(0); setEmailInvite('') }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
